/**
 * E2E Flow Converter
 * Issue #312: Convert E2E flow data to React Flow format
 */

import type { Node, Edge } from '@xyflow/react';
import type { E2EFlowPath, E2EFlowStep, FlowStepType } from '@veodiagram/shared';

export interface E2EFlowData {
  paths: E2EFlowPath[];
  metadata?: {
    projectId?: string;
    analyzedAt?: string;
    totalInteractions?: number;
  };
}

interface ConvertedFlow {
  nodes: Node[];
  edges: Edge[];
}

// Issue #389: Position types for layout persistence
export type NodePosition = { x: number; y: number };
export type NodePositions = Record<string, NodePosition>;

// Issue #437: Swimlane converter options
export interface SwimlaneOptions {
  customPositions?: NodePositions;
  resizable?: boolean;
}

/**
 * Convert E2E flow paths to React Flow nodes and edges
 * Issue #389: Now accepts optional customPositions to restore user-arranged layouts
 */
export function convertE2EFlowToReactFlow(
  data: E2EFlowData,
  customPositions?: NodePositions
): ConvertedFlow {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const nodeMap = new Map<string, boolean>();

  // Layout configuration
  const pathSpacing = 400; // Horizontal spacing between paths
  const stepSpacing = 120; // Vertical spacing between steps

  data.paths.forEach((path, pathIndex) => {
    const pathX = pathIndex * pathSpacing;

    // Add path header node
    const pathHeaderId = `path-header-${path.id}`;
    // Issue #389: Use custom position if available, otherwise use calculated position
    const headerPosition = customPositions?.[pathHeaderId] || { x: pathX, y: -80 };
    nodes.push({
      id: pathHeaderId,
      type: 'e2eFlowNode',
      position: headerPosition,
      data: {
        label: path.name || `Flow ${pathIndex + 1}`,
        type: 'user-action' as FlowStepType,
        description: path.description || `${path.steps.length} steps`,
        complete: path.complete,
        isPathHeader: true,  // Issue #389: Mark as path header for styling
      },
      style: {
        opacity: 0.8,
        // Issue #389: Removed pointerEvents: 'none' to allow dragging path headers
      },
    });

    // Issue #434: Track api-route data to propagate to subsequent api-response steps
    // Issue #435: Include typed fields
    let lastApiRouteData: {
      responseFields?: unknown[];
      requestSchemaName?: string;
      requestFields?: unknown[];
      requestTypedFields?: unknown[];
      responseTypedFields?: unknown[];
    } = {};

    // #1379 — per-step response-phase marker. See `computeResponsePhase`
    // for the algorithm; this drives the visual divider on the edge
    // crossing api-callback → first post-response step.
    const responsePhase = computeResponsePhase(path.steps);

    // Add step nodes
    path.steps.forEach((step: E2EFlowStep, stepIndex: number) => {
      const nodeId = `${path.id}-step-${stepIndex}`;

      // Avoid duplicate nodes
      if (nodeMap.has(nodeId)) return;
      nodeMap.set(nodeId, true);

      // Extract metadata from step.data
      const stepData = step.data as Record<string, unknown> || {};
      // Issue #434: Extract nested step data for api-call, api-route, database steps
      const apiCallData = (stepData.apiCallStepData || stepData.apiCall || {}) as Record<string, unknown>;
      const apiRouteData = (stepData.apiRouteStepData || stepData.apiRoute || {}) as Record<string, unknown>;
      const databaseData = (stepData.databaseStepData || stepData.database || {}) as Record<string, unknown>;

      // Issue #434: Track api-route data for propagation to api-response
      // Issue #435: Include typed fields
      if (step.type === 'api-route' && apiRouteData) {
        lastApiRouteData = {
          responseFields: apiRouteData.responseFields as unknown[] | undefined,
          requestSchemaName: (apiRouteData.requestSchemaName || apiRouteData.requestBodySchemaName) as string | undefined,
          requestFields: (apiRouteData.requestFields || apiRouteData.requestBodyFields) as unknown[] | undefined,
          requestTypedFields: apiRouteData.requestTypedFields as unknown[] | undefined,
          responseTypedFields: apiRouteData.responseTypedFields as unknown[] | undefined,
        };
      }

      // Issue #389: Use custom position if available, otherwise use calculated position
      const nodePosition = customPositions?.[nodeId] || {
        x: pathX,
        y: stepIndex * stepSpacing,
      };

      const node: Node = {
        id: nodeId,
        type: getNodeType(step.type),
        position: nodePosition,
        data: {
          label: step.name,
          type: step.type,
          description: step.description,
          sourceFile: step.sourceFile,
          sourceLine: step.sourceLine,
          isAsync: step.isAsync,
          duration: step.duration,
          error: step.error,
          // Issue #434: Extract method/endpoint from nested apiCallStepData or directly from stepData
          method: apiCallData.method || apiRouteData.method || stepData.method,
          endpoint: apiCallData.endpoint || apiRouteData.endpoint || apiRouteData.path || stepData.endpoint,
          pathParams: apiCallData.pathParams || apiRouteData.pathParams,
          queryParams: apiCallData.queryParams,
          // Issue #434: Request/Response schema and fields for edge detail panel
          // Issue #435: Include typed fields
          requestSchemaName: apiRouteData.requestSchemaName || apiRouteData.requestBodySchemaName,
          requestFields: apiRouteData.requestFields || apiRouteData.requestBodyFields,
          requestTypedFields: apiRouteData.requestTypedFields,
          // For api-response nodes, use propagated responseFields from preceding api-route
          responseFields: apiRouteData.responseFields || (step.type === 'api-response' ? lastApiRouteData.responseFields : undefined),
          responseTypedFields: apiRouteData.responseTypedFields || (step.type === 'api-response' ? lastApiRouteData.responseTypedFields : undefined),
          // API Response info
          statusCode: (stepData.apiResponseInfo as Record<string, unknown>)?.statusCode,
          responseType: (stepData.apiResponseInfo as Record<string, unknown>)?.type,
          contentType: (stepData.apiResponseInfo as Record<string, unknown>)?.contentType,
          // Issue #434: Propagate request schema info to api-response for edge panel
          // Issue #435: Include typed fields propagation
          propagatedRequestSchemaName: step.type === 'api-response' ? lastApiRouteData.requestSchemaName : undefined,
          propagatedRequestFields: step.type === 'api-response' ? lastApiRouteData.requestFields : undefined,
          propagatedRequestTypedFields: step.type === 'api-response' ? lastApiRouteData.requestTypedFields : undefined,
          // Database fields from nested databaseStepData
          operation: databaseData.operation || stepData.operation,
          model: databaseData.model || stepData.model,
          table: databaseData.table || stepData.table,
          fields: databaseData.fields,
          whereFields: databaseData.whereFields,
          triggerType: stepData.triggerType,
          // Issue #434: Store full step data objects for edge detail panel
          apiCallStepData: stepData.apiCallStepData || stepData.apiCall,
          apiRouteStepData: stepData.apiRouteStepData || stepData.apiRoute,
          apiResponseInfo: stepData.apiResponseInfo,
          databaseStepData: stepData.databaseStepData || stepData.database,
          isStart: stepIndex === 0,
          isEnd: stepIndex === path.steps.length - 1 && path.complete,
          // Issues #345-353: Pass enhanced fields to nodes
          navigationTargetDetails: stepData.navigationTargetDetails,
          domEffects: stepData.domEffects,
          clipboardContent: stepData.clipboardContent,
          formValidation: stepData.formValidation,
          apiSummary: stepData.apiSummary,
          affectedElements: stepData.affectedElements,
          evidence: step.evidence,
          // #1060-20C: branch metadata for UI affordance.
          //   `branch`        — legacy slot: 'success' | 'error' | 'then-N'
          //                     | 'else-N' | 'caller' (18C caller-side
          //                     stitching). Renderer keys on these.
          //   `ifBranch`      — 'A' | 'B' for the two arms of an if/else
          //                     emitted by stage 4 (RFC §risks §2).
          //   `ifBranchLine`  — source line of the gating `if` so the
          //                     tooltip can say "from if at line X".
          branch: stepData.branch,
          ifBranch: stepData.ifBranch,
          ifBranchLine: stepData.ifBranchLine,
          // #1379 — node-level response-phase marker so individual node
          // renderers can apply a subtle visual treatment (badge, muted
          // background) to client-side post-response steps.
          responsePhase: responsePhase[stepIndex],
        },
      };

      nodes.push(node);

      // Add edge to next step
      if (stepIndex < path.steps.length - 1) {
        const nextNodeId = `${path.id}-step-${stepIndex + 1}`;
        const nextStep = path.steps[stepIndex + 1];

        // #1379 — visual divider on the edge crossing api-callback →
        // first post-response step.  Edge gets a "↩ response received"
        // label, a thicker stroke, and a dashed style so the boundary
        // between server-side roundtrip and client-side response
        // handling is unmistakable on the diagram.  Inside the
        // post-response region we use a muted purple stroke for every
        // subsequent step; that matches the existing `branch: 'caller'`
        // styling vocabulary (caller-side stitching also uses purple).
        const isResponseBoundary =
          responsePhase[stepIndex] === 'pre' &&
          responsePhase[stepIndex + 1] === 'post-response';
        const isPostResponseEdge =
          responsePhase[stepIndex] === 'post-response' &&
          responsePhase[stepIndex + 1] === 'post-response';
        // #1382 item 4 — when the NEXT step lives on the analyzer-classified
        // `error` branch of an `if (res.ok) { ... } else { ... }` shape, the
        // edge entering it renders red. Lets the diagram make error paths
        // immediately distinguishable from success paths without changing
        // the existing post-response purple or step-type-derived colors.
        const nextStepData = (nextStep.data as Record<string, unknown> | undefined) ?? {};
        const isErrorBranchEdge = nextStepData.branch === 'error';
        const isSuccessBranchEdge = nextStepData.branch === 'success';
        const edgeStroke = isResponseBoundary || isPostResponseEdge
          ? '#9333ea'
          : isErrorBranchEdge
            ? '#dc2626'
            : isSuccessBranchEdge
              ? '#16a34a'
              : getEdgeColor(step.type, nextStep.type);

        edges.push({
          id: `${nodeId}->${nextNodeId}`,
          source: nodeId,
          target: nextNodeId,
          // Issue #434: Use clickableSmoothstep for wider click area
          type: 'clickableSmoothstep',
          animated: step.isAsync || false,
          style: {
            stroke: edgeStroke,
            strokeWidth: isResponseBoundary ? 3 : 2,
            strokeDasharray: isResponseBoundary ? '6 3' : undefined,
          },
          label: isResponseBoundary
            ? '↩ response received'
            : isErrorBranchEdge
              ? 'on error'
              : isSuccessBranchEdge
                ? 'on success'
                : step.isAsync ? 'async' : undefined,
          labelStyle: {
            fontSize: 10,
            fill: isResponseBoundary
              ? '#7e22ce'
              : isErrorBranchEdge
                ? '#991b1b'
                : isSuccessBranchEdge
                  ? '#166534'
                  : '#6b7280',
            fontWeight: isResponseBoundary || isErrorBranchEdge || isSuccessBranchEdge ? 600 : 400,
          },
          labelBgStyle: {
            fill: 'white',
            fillOpacity: 0.9,
          },
        });
      }
    });
  });

  return { nodes, edges };
}

/**
 * #1379 — per-step response-phase marker.
 *
 * Walks a flow's steps left-to-right and labels each with whether it
 * runs:
 *   - `'pre'`           — server-roundtrip phase (user-action, the
 *                         api-call → route → db → response → callback
 *                         chain itself, and any pre-await prep steps).
 *   - `'post-response'` — client-side response handling phase (state
 *                         mutations, dom updates, follow-up api-calls
 *                         that fire INSIDE the previous callback's
 *                         `.then(...)` / await continuation).
 *
 * Algorithm:
 *   - Start in `'pre'`.
 *   - When the current step is `'api-call'`, force `'pre'` for this step
 *     (it's a new request cycle — even if a previous callback flipped
 *     the flag, the new outgoing call resets the divider).
 *   - When the current step is `'api-callback'`, mark this step as
 *     `'pre'` (the callback itself is the response landing, not what
 *     the client does in response), but flip the flag so the NEXT step
 *     starts the `'post-response'` region.
 *   - All other step types inherit the current flag.
 *
 * Returned array is the same length as `steps`.  Pure function; safe
 * to call from both the sequential and swimlane converters.
 */
export function computeResponsePhase(
  steps: ReadonlyArray<E2EFlowStep>,
): Array<'pre' | 'post-response'> {
  const out: Array<'pre' | 'post-response'> = [];
  let inPostResponse = false;
  for (const step of steps) {
    if (step.type === 'api-call') {
      // A new outgoing request resets the post-response region: the
      // next round of response handling will be flagged when this
      // call's callback lands.
      inPostResponse = false;
      out.push('pre');
      continue;
    }
    out.push(inPostResponse ? 'post-response' : 'pre');
    if (step.type === 'api-callback') {
      inPostResponse = true;
    }
  }
  return out;
}

/**
 * Get the React Flow node type for a step type
 */
function getNodeType(type: FlowStepType): string {
  const typeMap: Record<FlowStepType, string> = {
    'user-action': 'user-action',
    'ui-component': 'ui-component',
    'api-call': 'api-call',
    'api-route': 'api-route',
    'backend-logic': 'backend-logic',          // Issue #390
    'service-call': 'service-call',
    'database': 'database',
    'external-api': 'external-api',
    'state-change': 'state-change',
    'navigation': 'navigation',
    'navigation-target': 'navigation-target',  // Issue #345
    'modal-container': 'modal-container',      // Issue #348
    'modal-outcome': 'modal-outcome',          // Issue #348
    'prop-callback': 'prop-callback',          // Issue #357
    'api-response': 'api-response',            // Issue #369
    'api-callback': 'api-callback',            // Issue #369
    'dom-update': 'dom-update',                // Issue #369
    'error': 'error',
    'cache': 'cache',
    'middleware': 'middleware',                 // Issue #898 F3
    'pre-action-gate': 'pre-action-gate',       // Issue #898 F5
  };

  return typeMap[type] || 'e2eFlowNode';
}

/**
 * Get edge color based on source and target step types
 */
function getEdgeColor(sourceType: FlowStepType, targetType: FlowStepType): string {
  // Error edges are red
  if (targetType === 'error') return '#dc2626';

  // API transitions
  if (sourceType === 'api-call' && targetType === 'api-route') return '#f59e0b';

  // Database transitions
  if (targetType === 'database') return '#ec4899';

  // Issue #369: API round-trip transitions
  if (targetType === 'api-response') return '#059669';  // Response from backend
  if (targetType === 'api-callback') return '#d97706';  // Callback handler
  if (targetType === 'dom-update') return '#16a34a';    // Final DOM change

  // Default blue
  return '#3b82f6';
}

/**
 * Calculate flow statistics
 */
export function calculateFlowStats(data: E2EFlowData): {
  totalPaths: number;
  completePaths: number;
  incompletePaths: number;
  totalSteps: number;
  stepsByType: Record<FlowStepType, number>;
} {
  const stepsByType: Record<FlowStepType, number> = {
    'user-action': 0,
    'ui-component': 0,
    'api-call': 0,
    'api-route': 0,
    'backend-logic': 0,      // Issue #390
    'service-call': 0,
    'database': 0,
    'external-api': 0,
    'state-change': 0,
    'navigation': 0,
    'navigation-target': 0,  // Issue #345
    'modal-container': 0,    // Issue #348
    'modal-outcome': 0,      // Issue #348
    'prop-callback': 0,      // Issue #357
    'api-response': 0,       // Issue #369
    'api-callback': 0,       // Issue #369
    'dom-update': 0,         // Issue #369
    'error': 0,
    'cache': 0,
    'middleware': 0,            // Issue #898 F3
    'pre-action-gate': 0,       // Issue #898 F5
  };

  let totalSteps = 0;
  let completePaths = 0;

  data.paths.forEach(path => {
    if (path.complete) completePaths++;

    path.steps.forEach((step: E2EFlowStep) => {
      totalSteps++;
      if (step.type in stepsByType) {
        stepsByType[step.type]++;
      }
    });
  });

  return {
    totalPaths: data.paths.length,
    completePaths,
    incompletePaths: data.paths.length - completePaths,
    totalSteps,
    stepsByType,
  };
}

/**
 * Issue #389: Extract node positions from React Flow nodes
 * Useful for saving layout to backend
 */
export function extractNodePositions(nodes: Node[]): NodePositions {
  const positions: NodePositions = {};
  nodes.forEach(node => {
    positions[node.id] = { x: node.position.x, y: node.position.y };
  });
  return positions;
}

// ============= Issue #427: Swimlane Layout =============

/**
 * Column type for swimlane layout
 */
export type SwimlaneColumn = 'client' | 'server' | 'database';

/**
 * Layout mode for E2E flow diagrams
 */
export type LayoutMode = 'sequential' | 'swimlane';

/**
 * Issue #428: Edge routing state for preventing overlaps
 */
interface EdgeRoutingState {
  /** Count of edges per column pair direction */
  columnPairEdges: Map<string, number>;
}

/**
 * Issue #428: Sequence tracking for chronological numbering
 */
interface SequenceState {
  /** Current sequence number */
  current: number;
  /** Start of async group (null if not in async group) */
  asyncGroupStart: number | null;
  /** Counter for async group suffix (a, b, c...) */
  asyncCounter: number;
}

/**
 * Issue #428: Edge offset configuration
 */
const EDGE_ROUTING_CONFIG = {
  /** Y-offset increment between parallel edges */
  offsetIncrement: 25,
  /** Maximum edges before resetting offset cycle */
  maxOffsetCycle: 6,
  /** Curvature range for bezier edges [min, max] */
  curvatureRange: [0.3, 0.6] as [number, number],
};

/**
 * Mapping of step types to swimlane columns
 */
const STEP_TO_COLUMN: Record<FlowStepType, SwimlaneColumn> = {
  // Client/Frontend steps
  'user-action': 'client',
  'ui-component': 'client',
  'api-call': 'client',
  'api-callback': 'client',
  'state-change': 'client',
  'navigation': 'client',
  'navigation-target': 'client',
  'modal-container': 'client',
  'modal-outcome': 'client',
  'dom-update': 'client',
  'prop-callback': 'client',
  // Server/Backend steps
  'api-route': 'server',
  'backend-logic': 'server',
  'service-call': 'server',
  'external-api': 'server',
  'cache': 'server',
  'api-response': 'server',
  // Database steps
  'database': 'database',
  // Error can be in any column, default to client
  'error': 'client',
  // Issue #898 F3: middleware runs server-side before the api-route.
  'middleware': 'server',
  // Issue #898 F5: pre-action confirm gate is client-side (modal/dialog).
  'pre-action-gate': 'client',
};

/**
 * Column configuration for swimlane layout
 */
const COLUMN_CONFIG: Record<SwimlaneColumn, {
  x: number;
  width: number;
  label: string;
  color: string;
  bgColor: string;
}> = {
  // #1074 G2 (#618): renamed swimlane headers from CLIENT/SERVER/DATABASE
  // to INTERACTION/SERVICE/DATA. First-look comprehension is noticeably
  // better — "Client" / "Server" describe locations (where the code runs);
  // "Interaction" / "Service" / "Data" describe what the lane represents.
  client: {
    x: 0,
    width: 280,
    label: 'INTERACTION',
    color: '#3b82f6',
    bgColor: 'rgba(59, 130, 246, 0.05)',
  },
  server: {
    x: 350,
    width: 280,
    label: 'SERVICE',
    color: '#10b981',
    bgColor: 'rgba(16, 185, 129, 0.05)',
  },
  database: {
    x: 700,
    width: 200,
    label: 'DATA',
    color: '#ec4899',
    bgColor: 'rgba(236, 72, 153, 0.05)',
  },
};

/**
 * Get the column for a step type
 */
export function getStepColumn(type: FlowStepType): SwimlaneColumn {
  return STEP_TO_COLUMN[type] || 'client';
}

/**
 * Get column configuration
 */
export function getColumnConfig(column: SwimlaneColumn) {
  return COLUMN_CONFIG[column];
}

/**
 * Get all column configurations
 */
export function getAllColumnConfigs() {
  return COLUMN_CONFIG;
}

/**
 * Issue #427: Convert E2E flow to swimlane layout
 * Organizes steps into three columns: Client | Server | Database
 * Issue #437: Added support for custom dimensions and resizable backgrounds
 */
export function convertE2EFlowToSwimlane(
  data: E2EFlowData,
  customPositionsOrOptions?: NodePositions | SwimlaneOptions
): ConvertedFlow {
  // Handle both old signature (just positions) and new options object
  let customPositions: NodePositions | undefined;
  let resizable = false;

  if (customPositionsOrOptions) {
    if ('customPositions' in customPositionsOrOptions || 'resizable' in customPositionsOrOptions) {
      // New options object
      const options = customPositionsOrOptions as SwimlaneOptions;
      customPositions = options.customPositions;
      resizable = options.resizable ?? false;
    } else {
      // Legacy: just positions
      customPositions = customPositionsOrOptions as NodePositions;
    }
  }

  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const nodeMap = new Map<string, boolean>();

  // Layout configuration
  const stepSpacing = 100; // Vertical spacing between steps
  const columnPadding = 40; // Padding within columns
  const headerHeight = 60; // Height reserved for column headers

  // Track Y positions per column for proper stacking
  const columnYPositions: Record<SwimlaneColumn, number> = {
    client: headerHeight + columnPadding,
    server: headerHeight + columnPadding,
    database: headerHeight + columnPadding,
  };

  // Track which columns are used
  const usedColumns = new Set<SwimlaneColumn>();

  // First pass: determine which columns are used
  data.paths.forEach((path) => {
    path.steps.forEach((step: E2EFlowStep) => {
      const column = getStepColumn(step.type);
      usedColumns.add(column);
    });
  });

  // Add column header nodes
  (['client', 'server', 'database'] as SwimlaneColumn[]).forEach((column) => {
    if (usedColumns.has(column)) {
      const config = COLUMN_CONFIG[column];
      const headerId = `swimlane-header-${column}`;
      const headerPosition = customPositions?.[headerId] || {
        x: config.x + config.width / 2 - 60,
        y: 0
      };

      nodes.push({
        id: headerId,
        type: 'swimlaneHeader',
        position: headerPosition,
        data: {
          label: config.label,
          column,
          color: config.color,
          bgColor: config.bgColor,
          width: config.width,
        },
        draggable: resizable, // Issue #437: Allow dragging when in editable mode
        selectable: resizable,
      });
    }
  });

  // Issue #428: Initialize edge routing state for non-overlapping edges
  const edgeRoutingState: EdgeRoutingState = {
    columnPairEdges: new Map(),
  };

  // Issue #428: Initialize sequence tracking for chronological numbering
  const sequenceState: SequenceState = {
    current: 1,
    asyncGroupStart: null,
    asyncCounter: 0,
  };

  // Process each path
  data.paths.forEach((path) => {
    let prevNodeId: string | null = null;
    let prevColumn: SwimlaneColumn | null = null;
    let prevStep: E2EFlowStep | null = null;

    // Issue #434: Track api-route data to propagate to subsequent api-response steps
    // Issue #435: Include typed fields
    let lastApiRouteData: {
      responseFields?: unknown[];
      requestSchemaName?: string;
      requestFields?: unknown[];
      requestTypedFields?: unknown[];
      responseTypedFields?: unknown[];
    } = {};

    // #1379 — per-step response-phase marker. See `computeResponsePhase`
    // for the algorithm; this drives the visual divider on the edge
    // crossing api-callback → first post-response step.
    const responsePhase = computeResponsePhase(path.steps);

    // Add step nodes
    path.steps.forEach((step: E2EFlowStep, stepIndex: number) => {
      const nodeId = `${path.id}-step-${stepIndex}`;

      // Avoid duplicate nodes
      if (nodeMap.has(nodeId)) return;
      nodeMap.set(nodeId, true);

      const column = getStepColumn(step.type);
      const config = COLUMN_CONFIG[column];

      // Extract metadata from step.data
      const stepData = step.data as Record<string, unknown> || {};
      // Issue #434: Extract nested step data for api-call, api-route, database steps
      const apiCallData = (stepData.apiCallStepData || stepData.apiCall || {}) as Record<string, unknown>;
      const apiRouteData = (stepData.apiRouteStepData || stepData.apiRoute || {}) as Record<string, unknown>;
      const databaseData = (stepData.databaseStepData || stepData.database || {}) as Record<string, unknown>;

      // Issue #434: Track api-route data for propagation to api-response
      // Issue #435: Include typed fields
      if (step.type === 'api-route' && apiRouteData) {
        lastApiRouteData = {
          responseFields: apiRouteData.responseFields as unknown[] | undefined,
          requestSchemaName: (apiRouteData.requestSchemaName || apiRouteData.requestBodySchemaName) as string | undefined,
          requestFields: (apiRouteData.requestFields || apiRouteData.requestBodyFields) as unknown[] | undefined,
          requestTypedFields: apiRouteData.requestTypedFields as unknown[] | undefined,
          responseTypedFields: apiRouteData.responseTypedFields as unknown[] | undefined,
        };
      }

      // Calculate position - use custom or calculate based on column
      const nodePosition = customPositions?.[nodeId] || {
        x: config.x + columnPadding,
        y: columnYPositions[column],
      };

      // Update column Y position for next node in same column
      if (!customPositions?.[nodeId]) {
        columnYPositions[column] += stepSpacing;
      }

      const node: Node = {
        id: nodeId,
        type: getNodeType(step.type),
        position: nodePosition,
        data: {
          label: step.name,
          type: step.type,
          description: step.description,
          sourceFile: step.sourceFile,
          sourceLine: step.sourceLine,
          isAsync: step.isAsync,
          duration: step.duration,
          error: step.error,
          // Issue #434: Extract method/endpoint from nested apiCallStepData or directly from stepData
          method: apiCallData.method || apiRouteData.method || stepData.method,
          endpoint: apiCallData.endpoint || apiRouteData.endpoint || apiRouteData.path || stepData.endpoint,
          pathParams: apiCallData.pathParams || apiRouteData.pathParams,
          queryParams: apiCallData.queryParams,
          // Issue #434: Request/Response schema and fields for edge detail panel
          // Issue #435: Include typed fields
          requestSchemaName: apiRouteData.requestSchemaName || apiRouteData.requestBodySchemaName,
          requestFields: apiRouteData.requestFields || apiRouteData.requestBodyFields,
          requestTypedFields: apiRouteData.requestTypedFields,
          // For api-response nodes, use propagated responseFields from preceding api-route
          responseFields: apiRouteData.responseFields || (step.type === 'api-response' ? lastApiRouteData.responseFields : undefined),
          responseTypedFields: apiRouteData.responseTypedFields || (step.type === 'api-response' ? lastApiRouteData.responseTypedFields : undefined),
          // API Response info
          statusCode: (stepData.apiResponseInfo as Record<string, unknown>)?.statusCode,
          responseType: (stepData.apiResponseInfo as Record<string, unknown>)?.type,
          contentType: (stepData.apiResponseInfo as Record<string, unknown>)?.contentType,
          // Issue #434: Propagate request schema info to api-response for edge panel
          // Issue #435: Include typed fields propagation
          propagatedRequestSchemaName: step.type === 'api-response' ? lastApiRouteData.requestSchemaName : undefined,
          propagatedRequestFields: step.type === 'api-response' ? lastApiRouteData.requestFields : undefined,
          propagatedRequestTypedFields: step.type === 'api-response' ? lastApiRouteData.requestTypedFields : undefined,
          // Database fields from nested databaseStepData
          operation: databaseData.operation || stepData.operation,
          model: databaseData.model || stepData.model,
          table: databaseData.table || stepData.table,
          fields: databaseData.fields,
          whereFields: databaseData.whereFields,
          triggerType: stepData.triggerType,
          // Issue #434: Store full step data objects for edge detail panel
          apiCallStepData: stepData.apiCallStepData || stepData.apiCall,
          apiRouteStepData: stepData.apiRouteStepData || stepData.apiRoute,
          apiResponseInfo: stepData.apiResponseInfo,
          databaseStepData: stepData.databaseStepData || stepData.database,
          isStart: stepIndex === 0,
          isEnd: stepIndex === path.steps.length - 1 && path.complete,
          // Issues #345-353: Pass enhanced fields to nodes
          navigationTargetDetails: stepData.navigationTargetDetails,
          domEffects: stepData.domEffects,
          clipboardContent: stepData.clipboardContent,
          formValidation: stepData.formValidation,
          apiSummary: stepData.apiSummary,
          affectedElements: stepData.affectedElements,
          evidence: step.evidence,
          // Issue #427: Add column info for styling
          swimlaneColumn: column,
          // #1060-20C: branch metadata for UI affordance (see sequential
          // converter above for the schema of these three fields).
          branch: stepData.branch,
          ifBranch: stepData.ifBranch,
          ifBranchLine: stepData.ifBranchLine,
          // #1379 — node-level response-phase marker. See sequential
          // converter above for the algorithm.
          responsePhase: responsePhase[stepIndex],
        },
      };

      nodes.push(node);

      // Add edge from previous step
      if (prevNodeId && prevColumn) {
        const isCrossColumn = prevColumn !== column;
        const pairKey = `${prevColumn}-${column}`;

        // Issue #428: Calculate sequence number for chronological ordering
        const sequenceInfo = calculateStepSequence(prevStep, step, sequenceState);

        // Issue #428: Calculate edge offset for cross-column edges
        let edgeOffset = 0;
        let edgeCurvature = 0.4;

        if (isCrossColumn) {
          const offsetInfo = calculateEdgeOffset(pairKey, edgeRoutingState, EDGE_ROUTING_CONFIG);
          edgeOffset = offsetInfo.offset;
          edgeCurvature = offsetInfo.curvature;

          // Track this edge for next offset calculation
          const count = edgeRoutingState.columnPairEdges.get(pairKey) || 0;
          edgeRoutingState.columnPairEdges.set(pairKey, count + 1);
        }

        // Issue #428: Format label with sequence number
        const baseLabel = isCrossColumn
          ? getCrossColumnLabel(prevStep?.type, step.type)
          : (prevStep?.isAsync ? 'async' : undefined);
        const numberedLabel = formatSequenceLabel(sequenceInfo, baseLabel);

        const edgeColor = getSwimlaneEdgeColor(prevStep?.type, step.type, isCrossColumn);

        // #1379 — visual divider on the edge crossing api-callback →
        // first post-response step.  Same intent as in the sequential
        // converter: dashed, thicker, purple stroke + "↩ response
        // received" label so the request/response boundary is
        // unmistakable.  Override the cross-column/async label only on
        // the boundary edge so the existing sequence-number labelling
        // is preserved for every other edge.
        const isResponseBoundary =
          responsePhase[stepIndex - 1] === 'pre' &&
          responsePhase[stepIndex] === 'post-response';
        // #1382 item 4 — swimlane parity. Mirror the sequential converter:
        // when the NEXT step (this iteration's `step`) is on the analyzer's
        // semantic 'error'/'success' branch, override stroke + label so the
        // unhappy/happy path is visually distinguishable in swimlane mode
        // too. Post-response purple still wins (structural divider).
        const swimlaneNextData = (step.data as Record<string, unknown> | undefined) ?? {};
        const swimlaneIsErrorBranchEdge = swimlaneNextData.branch === 'error';
        const swimlaneIsSuccessBranchEdge = swimlaneNextData.branch === 'success';
        const finalEdgeColor = isResponseBoundary
          ? '#9333ea'
          : swimlaneIsErrorBranchEdge
            ? '#dc2626'
            : swimlaneIsSuccessBranchEdge
              ? '#16a34a'
              : edgeColor;
        const finalLabel = isResponseBoundary
          ? '↩ response received'
          : swimlaneIsErrorBranchEdge
            ? 'on error'
            : swimlaneIsSuccessBranchEdge
              ? 'on success'
              : numberedLabel;

        edges.push({
          id: `${prevNodeId}->${nodeId}`,
          source: prevNodeId,
          target: nodeId,
          // Issue #428: Use custom edge type for cross-column, smoothstep for same-column
          type: isCrossColumn ? 'swimlaneCrossColumn' : 'smoothstep',
          animated: isCrossColumn || prevStep?.isAsync || false,
          // Issue #428: Include offset and sequence data for custom edge
          data: {
            sequenceNumber: sequenceInfo.sequence,
            parallelSuffix: sequenceInfo.suffix,
            yOffset: edgeOffset,
            curvature: edgeCurvature,
            transitionType: pairKey,
            isAsync: prevStep?.isAsync || false,
          },
          style: {
            stroke: finalEdgeColor,
            strokeWidth: isResponseBoundary ? 3 : (isCrossColumn ? 2.5 : 2),
            strokeDasharray: isResponseBoundary ? '6 3' : undefined,
          },
          label: finalLabel,
          labelStyle: {
            fontSize: 10,
            fill: isResponseBoundary
              ? '#7e22ce'
              : swimlaneIsErrorBranchEdge
                ? '#991b1b'
                : swimlaneIsSuccessBranchEdge
                  ? '#166534'
                  : (isCrossColumn ? '#374151' : '#6b7280'),
            fontWeight: (isCrossColumn || isResponseBoundary || swimlaneIsErrorBranchEdge || swimlaneIsSuccessBranchEdge) ? 600 : 400,
          },
          labelBgStyle: {
            fill: 'white',
            fillOpacity: 0.95,
          },
          labelBgPadding: [4, 2] as [number, number],
          // Issue #428: Use appropriate handles for cross-column routing
          sourceHandle: isCrossColumn ? getSourceHandle(prevColumn, column) : 'bottom',
          targetHandle: isCrossColumn ? getTargetHandle(prevColumn, column) : 'top',
        });
      }

      prevNodeId = nodeId;
      prevColumn = column;
      prevStep = step;
    });
  });

  // Background panels removed — only column headers are kept for a cleaner look

  return { nodes, edges };
}

/**
 * Get edge color for swimlane layout
 */
function getSwimlaneEdgeColor(
  sourceType: FlowStepType | undefined,
  targetType: FlowStepType,
  isCrossColumn: boolean
): string {
  if (!sourceType) return '#3b82f6';

  const sourceColumn = getStepColumn(sourceType);
  const targetColumn = getStepColumn(targetType);

  // Cross-column edge colors
  if (isCrossColumn) {
    // Client to Server (API request)
    if (sourceColumn === 'client' && targetColumn === 'server') {
      return '#f59e0b'; // Orange - outgoing request
    }
    // Server to Database
    if (sourceColumn === 'server' && targetColumn === 'database') {
      return '#ec4899'; // Pink - database operation
    }
    // Database to Server (response)
    if (sourceColumn === 'database' && targetColumn === 'server') {
      return '#a855f7'; // Purple - DB response
    }
    // Server to Client (API response)
    if (sourceColumn === 'server' && targetColumn === 'client') {
      return '#059669'; // Green - API response
    }
  }

  // Error edges
  if (targetType === 'error') return '#dc2626';

  // Within-column edges
  return getEdgeColor(sourceType, targetType);
}

/**
 * Get label for cross-column edges
 */
function getCrossColumnLabel(
  sourceType: FlowStepType | undefined,
  targetType: FlowStepType
): string | undefined {
  if (!sourceType) return undefined;

  const sourceColumn = getStepColumn(sourceType);
  const targetColumn = getStepColumn(targetType);

  // Client to Server
  if (sourceColumn === 'client' && targetColumn === 'server') {
    if (sourceType === 'api-call') return 'HTTP';
    return 'request';
  }

  // Server to Database
  if (sourceColumn === 'server' && targetColumn === 'database') {
    return 'query';
  }

  // Database to Server
  if (sourceColumn === 'database' && targetColumn === 'server') {
    return 'result';
  }

  // Server to Client
  if (sourceColumn === 'server' && targetColumn === 'client') {
    return 'response';
  }

  return undefined;
}

/**
 * Issue #428: Calculate edge offset to prevent overlapping
 * Returns Y-offset and curvature for edge routing
 */
function calculateEdgeOffset(
  pairKey: string,
  state: EdgeRoutingState,
  config: typeof EDGE_ROUTING_CONFIG
): { offset: number; curvature: number } {
  const count = state.columnPairEdges.get(pairKey) || 0;

  // Cycle through offsets to spread edges
  const cycleIndex = count % config.maxOffsetCycle;
  // Alternate positive/negative for visual distribution
  const direction = cycleIndex % 2 === 0 ? 1 : -1;
  const magnitude = Math.floor(cycleIndex / 2) + 1;

  const offset = direction * magnitude * config.offsetIncrement;

  // Vary curvature slightly for additional separation
  const curvatureRange = config.curvatureRange[1] - config.curvatureRange[0];
  const curvatureVariation = (cycleIndex / config.maxOffsetCycle) * curvatureRange;
  const curvature = config.curvatureRange[0] + curvatureVariation;

  return { offset, curvature };
}

/**
 * Issue #428: Calculate sequence number for a step transition
 */
function calculateStepSequence(
  prevStep: E2EFlowStep | null,
  currentStep: E2EFlowStep,
  state: SequenceState
): { sequence: number; suffix?: string } {
  // Start async group if previous step was async
  if (prevStep?.isAsync && state.asyncGroupStart === null) {
    state.asyncGroupStart = state.current;
    state.asyncCounter = 0;
  }

  // If we're in an async group
  if (state.asyncGroupStart !== null) {
    const suffix = String.fromCharCode(97 + state.asyncCounter); // a, b, c...
    const result = { sequence: state.asyncGroupStart, suffix };
    state.asyncCounter++;

    // End async group when current step is not async
    if (!currentStep.isAsync) {
      state.asyncGroupStart = null;
      state.current++;
    }

    return result;
  }

  // Normal sequential numbering
  const result = { sequence: state.current };
  state.current++;
  return result;
}

/**
 * Issue #428: Format sequence number into label
 */
function formatSequenceLabel(
  sequenceInfo: { sequence: number; suffix?: string },
  baseLabel: string | undefined
): string {
  const num = sequenceInfo.suffix
    ? `${sequenceInfo.sequence}${sequenceInfo.suffix}`
    : `${sequenceInfo.sequence}`;

  return baseLabel ? `${num}: ${baseLabel}` : num;
}

/**
 * Issue #428: Get source handle ID based on column transition
 */
function getSourceHandle(
  sourceColumn: SwimlaneColumn,
  targetColumn: SwimlaneColumn
): string {
  // Outgoing edges (left to right) use right source handle
  if (
    (sourceColumn === 'client' && targetColumn === 'server') ||
    (sourceColumn === 'server' && targetColumn === 'database')
  ) {
    return 'right';
  }
  // Return edges (right to left) use left source handle
  if (
    (sourceColumn === 'server' && targetColumn === 'client') ||
    (sourceColumn === 'database' && targetColumn === 'server')
  ) {
    return 'left-source';  // Fixed: was 'left' which is a target handle
  }
  return 'bottom';
}

/**
 * Issue #428: Get target handle ID based on column transition
 */
function getTargetHandle(
  sourceColumn: SwimlaneColumn,
  targetColumn: SwimlaneColumn
): string {
  // Incoming from left column use left target handle
  if (
    (sourceColumn === 'client' && targetColumn === 'server') ||
    (sourceColumn === 'server' && targetColumn === 'database')
  ) {
    return 'left';
  }
  // Return edges target right side
  if (
    (sourceColumn === 'server' && targetColumn === 'client') ||
    (sourceColumn === 'database' && targetColumn === 'server')
  ) {
    return 'right-target';  // Fixed: was 'right' which is a source handle
  }
  return 'top';
}
